import React from 'react';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faChartLine } from '@fortawesome/pro-regular-svg-icons';
import { twMerge } from 'tailwind-merge';

type AnalyticsEmptyStateProps = {
  hasGeographies: boolean;
  hasCodeSets: boolean;
  className?: string;
};

/**
 * Empty state component for Analytics tables
 * Shows appropriate message based on what data is missing
 * Designed to fill the table body area without additional borders or backgrounds
 */
const AnalyticsEmptyState = ({
  hasGeographies,
  hasCodeSets,
  className,
}: AnalyticsEmptyStateProps) => {
  // Determine the appropriate message based on what's missing
  const getMessage = () => {
    if (!hasGeographies && !hasCodeSets) {
      return {
        title: 'Select geographies and code sets to view data',
        description:
          'Choose one or more geographies and code sets from the controls above to see analytics data in this table.',
      };
    }

    if (!hasGeographies) {
      return {
        title: 'Select geographies to view data',
        description:
          'Choose one or more geographies from the controls above to see analytics data in this table.',
      };
    }

    if (!hasCodeSets) {
      return {
        title: 'Select code sets to view data',
        description:
          'Choose one or more code sets from the controls above to see analytics data in this table.',
      };
    }

    return {
      title: 'No data available',
      description:
        'No analytics data is available for the selected criteria. Try adjusting your filters or selections.',
    };
  };

  const { title, description } = getMessage();

  return (
    <div
      className={twMerge(
        'flex flex-col items-center justify-center text-center gap-4',
        className
      )}
      data-testid="analytics-empty-state"
    >
      <Icon icon={faChartLine} size="2xl" className="w-12 h-12 text-neutral-400" />
      <div className="flex flex-col gap-2 max-w-md">
        <h4 className="text-base font-semibold text-neutral-700">{title}</h4>
        <p className="text-sm text-neutral-600 leading-relaxed">
          {description}
        </p>
      </div>
    </div>
  );
};

export default AnalyticsEmptyState;
